import React, { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface SendEmailDrawerProps {
  open: boolean;
  onClose: () => void;
  policyId: number;
  policyName: string;
}

const SendEmailDrawer: React.FC<SendEmailDrawerProps> = ({
  open,
  onClose,
  policyId,
  policyName,
}) => {
  const [emailText, setEmailText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const theme = useTheme();

  // Use atlas theme breakpoints: below md (481px) for mobile
  const isBelowMd = useMediaQuery(theme.breakpoints.down("md"));

  // Email validation function
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Check if email is valid
  const isEmailValid = emailText.trim() && validateEmail(emailText.trim());

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmailText(value);

    // Validate email if user has entered something
    if (value.trim()) {
      if (!validateEmail(value.trim())) {
        setEmailError("Invalid email");
      } else {
        setEmailError("");
      }
    } else {
      setEmailError("");
    }
  };

  const handleSendEmail = async () => {
    if (!isEmailValid) {
      return;
    }

    setIsLoading(true);
    try {
      // TODO: Implement actual email sending logic
      console.log("Sending email:", {
        policyId,
        policyName,
        emailText,
      });

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Reset form and close drawer
      setEmailText("");
      onClose();
    } catch (error) {
      console.error("Error sending email:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setEmailText("");
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: isBelowMd ? "100vw" : 480,
            top: 64,
            height: "calc(100vh - 64px)",
          },
        },
        backdrop: {
          sx: {
            top: 64,
            height: "calc(100vh - 64px)",
          },
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          component="header"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1.5, // Header vertical padding
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
            }}
          >
            Email policy for employee attendance
          </Typography>
          <IconButton 
            onClick={onClose} 
            size="small"
            aria-label="Close drawer"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Body/Content */}
        <Box
          component="main"
          sx={{
            flex: 1,
            py: 2, // Body vertical padding
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 1, // 8px gap
            alignSelf: "stretch",
            flexGrow: 1,
          }}
        >
          {/* Recipient's email address label */}
          <Typography variant="body1" sx={{ mb: 0 }}>
            Recipient's email address
          </Typography>

          {/* Email input field */}
          <TextField
            fullWidth
            variant="outlined"
            value={emailText}
            onChange={handleEmailChange}
            error={!!emailError}
            helperText={emailError}
          />
        </Box>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            borderTop: 1,
            borderColor: "divider",
            backgroundColor: "grey.50",
            mx: -3, // Extend to full drawer width
            px: 3, // Add back internal padding
          }}
        >
          <Box sx={{ pt: 2, pb: 0 }}>
            {" "}
            {/* Footer content padding */}
            <Stack
              direction={isBelowMd ? "column" : "row"}
              spacing={2}
              justifyContent={isBelowMd ? "stretch" : "space-between"}
              alignItems={isBelowMd ? "stretch" : "flex-end"}
            >
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={isLoading}
                fullWidth={isBelowMd}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSendEmail}
                disabled={!isEmailValid || isLoading}
                fullWidth={isBelowMd}
              >
                {isLoading ? "Sending..." : "Send"}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default SendEmailDrawer;
